import moment, { Moment } from 'moment/moment'

import { MasterCampaignPlan } from 'api/entities/MasterCampaignPlan'
import { IntervalUnit, MasterCampaignPlanCampaignBuilder, MasterCampaignStub } from 'api/entities/MasterCampaignStub'

export function calculateTotal(intervalBudget?: number, intervalDuration?: number): number {
  if (!intervalBudget || !intervalDuration) {
    return 0
  }

  return intervalBudget * intervalDuration
}

export const getMasterCampaignStubAverageMonthlyBudgetOld = (masterCampaignPlan: MasterCampaignPlan, masterCampaignStub: MasterCampaignStub) => {
  return getMasterCampaignBuildersAverageMonthlyBudget(masterCampaignPlan.aiUsedDurationInMonths, masterCampaignStub.campaignBuilders)
}

export const getMasterCampaignStubAverageMonthlyBudget = (masterCampaignStub: MasterCampaignStub) => {
  const durationMonths = getTotalMonthRange(masterCampaignStub.campaignBuilders)
  return getMasterCampaignBuildersAverageMonthlyBudget(durationMonths, masterCampaignStub.campaignBuilders)
}

export const getMasterCampaignBuildersAverageMonthlyBudget = (durationInMonths: number, builders?: MasterCampaignPlanCampaignBuilder[]) => {
  if (!builders || builders.length === 0) {
    return 0
  }

  const totalBudget = builders.reduce((total, builder) => {
    return total + calculateTotal(builder.intervalBudget, builder.intervalDuration)
  }, 0)

  return totalBudget / durationInMonths
}

export const getMasterCampaignStubStartDate = (masterCampaignStub?: MasterCampaignStub): Moment | undefined => {
  const builders = [...(masterCampaignStub?.campaignBuilders ?? [])]
  if (!builders || builders.length === 0) {
    return undefined
  }

  const earliestCampaign = builders.sort((a, b) => {
    return moment(a.startDate).isBefore(moment(b.startDate)) ? -1 : 1
  })[0]
  if (!earliestCampaign || !earliestCampaign.startDate) {
    return undefined
  }

  return moment(earliestCampaign.startDate)
}

export const getEarliestStartDate = (campaigns: MasterCampaignPlanCampaignBuilder[]): Moment | undefined => {
  if (!campaigns || campaigns.length === 0) {
    return undefined
  }

  const orderedCampaigns = [...campaigns].sort((a, b) => {
    return moment(a.startDate).isBefore(moment(b.startDate)) ? -1 : 1
  })

  const earliestCampaign = orderedCampaigns[0]

  if (!earliestCampaign || !earliestCampaign.startDate) {
    return undefined
  }

  return moment(earliestCampaign.startDate)
}

export const getLatestEndDate = (campaigns: MasterCampaignPlanCampaignBuilder[]) => {
  if (!campaigns || campaigns.length === 0) {
    return undefined
  }

  const orderedCampaigns = [...campaigns].sort((a, b) => {
    return getEndDate(a)?.isAfter(getEndDate(b)) ? -1 : 1
  })
  const latestCampaign = orderedCampaigns[0]

  if (!latestCampaign) {
    return undefined
  }

  return getEndDate(latestCampaign)
}

export const getEndDate = (item: MasterCampaignPlanCampaignBuilder): Moment | undefined => {
  if (!item || !item.startDate) {
    return undefined
  }

  let intervalUnit: IntervalUnit
  let intervalFrequency: number
  let totalIntervals: number

  const builder = item as MasterCampaignPlanCampaignBuilder

  if (builder.intervalUnit && builder.intervalFrequency && builder.intervalDuration) {
    intervalUnit = builder.intervalUnit
    intervalFrequency = builder.intervalFrequency
    totalIntervals = builder.intervalDuration
  } else {
    return undefined
  }

  let momentUnit = 'month'
  if (intervalUnit === IntervalUnit.Day) {
    momentUnit = 'day'
  } else if (intervalUnit === IntervalUnit.Week) {
    momentUnit = 'week'
  } else if (intervalUnit === IntervalUnit.Month) {
    momentUnit = 'month'
  }

  return moment(builder.startDate).add(momentUnit as moment.unitOfTime.DurationConstructor, totalIntervals * intervalFrequency)
}

export const getTotalMonthRange = (campaigns: MasterCampaignPlanCampaignBuilder[]): number => {
  if (!campaigns || campaigns.length === 0) {
    return 0
  }

  const startDate = getEarliestStartDate(campaigns)
  const endDate = getLatestEndDate(campaigns)
  if (!startDate || !endDate) {
    return 0
  }
  return endDate.endOf('month').diff(startDate.endOf('month'), 'months')
}
